import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { fetchHrLoanAdvanceSettings, updateHrLoanAdvanceSettings } from "../../../../apis/hrApi";

export default function LoanAndAdvancesTab() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form states matching fields in the design
  const [newEmployeeLoanEligibleInMonth, setNewEmployeeLoanEligibleInMonth] = useState(0);
  const [newEmployeeLoanSalaryCount, setNewEmployeeLoanSalaryCount] = useState(2);
  const [newEmployeeRepaymentDurationInMonth, setNewEmployeeRepaymentDurationInMonth] = useState(6);

  const [oldEmployeeFromMonth, setOldEmployeeFromMonth] = useState(1);
  const [oldEmployeeToMonth, setOldEmployeeToMonth] = useState(10);
  const [oldEmployeeLoanSalaryCount, setOldEmployeeLoanSalaryCount] = useState(30000);
  const [oldEmployeeRepaymentDurationInMonth, setOldEmployeeRepaymentDurationInMonth] = useState(12);

  const [veteranEmployeeFromMonth, setVeteranEmployeeFromMonth] = useState(11);
  const [veteranEmployeeMaxAmount, setVeteranEmployeeMaxAmount] = useState(30000);
  const [veteranEmployeeRepaymentDurationInMonth, setVeteranEmployeeRepaymentDurationInMonth] = useState(24);

  // Load current settings from API on mount
  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchHrLoanAdvanceSettings();
      if (res.success && res.data) {
        let data = res.data;
        if (Array.isArray(res.data)) {
          data = res.data[0];
        } else if (res.data.loanAdvanceSettings) {
          data = res.data.loanAdvanceSettings;
          if (Array.isArray(data)) data = data[0];
        } else if (res.data.loanAdvanceSetting) {
          data = res.data.loanAdvanceSetting;
        } else if (res.data.settings) {
          data = res.data.settings;
          if (Array.isArray(data)) data = data[0];
        } else if (Array.isArray(res.data.list)) {
          data = res.data.list[0];
        }

        if (data) {
          setNewEmployeeLoanEligibleInMonth(data.newEmployeeLoanEligibleInMonth ?? 0);
          setNewEmployeeLoanSalaryCount(data.newEmployeeLoanSalaryCount ?? 2);
          setNewEmployeeRepaymentDurationInMonth(data.newEmployeeRepaymentDurationInMonth ?? 6);

          setOldEmployeeFromMonth(data.oldEmployeeFromMonth ?? 1);
          setOldEmployeeToMonth(data.oldEmployeeToMonth ?? 10);
          setOldEmployeeLoanSalaryCount(data.oldEmployeeLoanSalaryCount ?? 30000);
          setOldEmployeeRepaymentDurationInMonth(data.oldEmployeeRepaymentDurationInMonth ?? 12);

          setVeteranEmployeeFromMonth(data.veteranEmployeeFromMonth ?? 11);
          setVeteranEmployeeMaxAmount(data.veteranEmployeeMaxAmount ?? 30000);
          setVeteranEmployeeRepaymentDurationInMonth(data.veteranEmployeeRepaymentDurationInMonth ?? 24);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Loan & Advance settings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save Settings
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        newEmployeeLoanEligibleInMonth: Number(newEmployeeLoanEligibleInMonth),
        newEmployeeLoanSalaryCount: Number(newEmployeeLoanSalaryCount),
        newEmployeeRepaymentDurationInMonth: Number(newEmployeeRepaymentDurationInMonth),
        oldEmployeeFromMonth: Number(oldEmployeeFromMonth),
        oldEmployeeToMonth: Number(oldEmployeeToMonth),
        oldEmployeeLoanSalaryCount: Number(oldEmployeeLoanSalaryCount),
        oldEmployeeRepaymentDurationInMonth: Number(oldEmployeeRepaymentDurationInMonth),
        veteranEmployeeFromMonth: Number(veteranEmployeeFromMonth),
        veteranEmployeeMaxAmount: Number(veteranEmployeeMaxAmount),
        veteranEmployeeRepaymentDurationInMonth: Number(veteranEmployeeRepaymentDurationInMonth)
      };

      const res = await updateHrLoanAdvanceSettings(payload);
      if (res.success) {
        toast.success("Loan & Advance settings saved successfully!");
        loadSettings();
      } else {
        toast.error(res.message || "Failed to save settings");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-6 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-12 mx-auto max-w-5xl mt-2 select-none animate-in fade-in duration-300">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-semibold text-[#212121] mx-auto">Loan & Advance Settings</h2>
        {isLoading && (
          <span className="text-xs text-gray-400 font-medium animate-pulse absolute right-8">Loading...</span>
        )}
      </div>

      <div className="bg-[#FFFFFF] p-6 rounded-[20px] space-y-6 border border-[#D2D2D2]">
        
        {/* New Employee */}
        <div className="border border-[#D2D2D2] rounded-xl p-5 relative pt-6 mt-2 bg-[#FFFFFD]">
          <h3 className="text-sm font-semibold text-[#212121] bg-white px-2 absolute -top-3 left-4">
            New Employee
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                New Employee Loan Eligible In Month
              </label>
              <input
                type="number"
                value={newEmployeeLoanEligibleInMonth}
                onChange={(e) => setNewEmployeeLoanEligibleInMonth(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                New Employee Loan Salary Count
              </label>
              <input
                type="number"
                value={newEmployeeLoanSalaryCount}
                onChange={(e) => setNewEmployeeLoanSalaryCount(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                New Employee Repayment Duration In Month
              </label>
              <input
                type="number"
                value={newEmployeeRepaymentDurationInMonth}
                onChange={(e) => setNewEmployeeRepaymentDurationInMonth(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
          </div>
        </div>

        {/* Old Employee */}
        <div className="border border-[#D2D2D2] rounded-xl p-5 relative pt-6 mt-6 bg-[#FFFFFD]">
          <h3 className="text-sm font-semibold text-[#212121] bg-white px-2 absolute -top-3 left-4">
            Old Employee
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Old Employee From Month
              </label>
              <input
                type="number"
                value={oldEmployeeFromMonth}
                onChange={(e) => setOldEmployeeFromMonth(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Old Employee To Month
              </label>
              <input
                type="number"
                value={oldEmployeeToMonth}
                onChange={(e) => setOldEmployeeToMonth(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Old Employee Loan Salary Count
              </label>
              <input
                type="number"
                value={oldEmployeeLoanSalaryCount}
                onChange={(e) => setOldEmployeeLoanSalaryCount(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Old Employee Repayment Duration In Month
              </label>
              <input
                type="number"
                value={oldEmployeeRepaymentDurationInMonth}
                onChange={(e) => setOldEmployeeRepaymentDurationInMonth(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
          </div>
        </div>

        {/* Veteran Employee */}
        <div className="border border-[#D2D2D2] rounded-xl p-5 relative pt-6 mt-6 bg-[#FFFFFD]">
          <h3 className="text-sm font-semibold text-[#212121] bg-white px-2 absolute -top-3 left-4">
            Veteran Employee
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Veteran Employee From Month
              </label>
              <input
                type="number"
                value={veteranEmployeeFromMonth}
                onChange={(e) => setVeteranEmployeeFromMonth(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Veteran Employee Max Amount
              </label>
              <input
                type="number"
                value={veteranEmployeeMaxAmount}
                onChange={(e) => setVeteranEmployeeMaxAmount(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Veteran Employee Repayment Duration In Month
              </label>
              <input
                type="number"
                value={veteranEmployeeRepaymentDurationInMonth}
                onChange={(e) => setVeteranEmployeeRepaymentDurationInMonth(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
              />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="bg-black hover:bg-neutral-800 text-white text-xs font-semibold px-6 py-2.5 rounded-lg transition cursor-pointer active:scale-95 disabled:opacity-60"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>

      </div>
    </div>
  );
}
